import { FC, ReactNode } from 'react';
import Icon from 'components/global/Icon/Primary';
import { Action, BannerButton, BannerContainer, Message } from './styles';

export interface BannerProps {
  /**
   * Announcement copy. Accepts a ReactNode, so a leading icon
   * can be composed inline — e.g. `<><Icon name="discount" /> 30% off</>`.
   */
  message: ReactNode | string;
  /** Inline action label, rendered as a TextButton. */
  actionText?: string;
  /** Trailing arrow after the action label */
  showIcon?: boolean;
  dark?: boolean;
  href?: string;
  onClick?: () => void;
  className?: string;
  'aria-label'?: string;
}

const Banner: FC<BannerProps> = ({
  message,
  actionText,
  showIcon = true,
  dark,
  href,
  onClick,
  className,
  'aria-label': ariaLabel,
}) => {
  const interactive = Boolean(href || onClick);

  // Interactive with a visible action label: plain message + inline
  // TextButton whose stretched ::after makes the whole strip clickable.
  if (interactive && actionText) {
    return (
      <BannerContainer $dark={dark} className={className}>
        <Message>{message}</Message>
        <Action
          size='sm'
          light={dark}
          href={href}
          onClick={onClick}
          aria-label={ariaLabel}
          trailingIcon={
            showIcon ? <Icon name='arrow-right' size='sm' /> : undefined
          }
        >
          {actionText}
        </Action>
      </BannerContainer>
    );
  }

  // Interactive with no action label: the entire strip is the TextButton.
  if (interactive) {
    return (
      <BannerButton
        $dark={dark}
        size='sm'
        light={dark}
        href={href}
        onClick={onClick}
        className={className}
        aria-label={ariaLabel}
      >
        {message}
      </BannerButton>
    );
  }

  // Non-interactive informational strip.
  return (
    <BannerContainer $dark={dark} className={className}>
      <Message>{message}</Message>
    </BannerContainer>
  );
};

export default Banner;

/**
 * HOW TO IMPLEMENT - BANNER
 * ---------------
 * Full-width, single-line promotional / informational strip. Place at the top
 * of a page or section, edge-to-edge. When `href` or `onClick` is supplied the
 * ENTIRE strip is the click target (a single TextButton — never a nested
 * control): with `actionText` it renders an inline action whose stretched
 * hit-area covers the strip; without it the whole strip is the TextButton.
 *
 * @example Bright (default) with link action — whole strip clicks through
 * <Banner message="Free shipping on orders over $50." actionText="Shop now" href="/shop" />
 *
 * @example Dark variant with an on-page action
 * <Banner dark message="Your trial ends soon." actionText="Resubscribe" onClick={handleResubscribe} />
 *
 * @example Clickable strip with no action label
 * <Banner message="Members Only • 25% Off New Products" href="/new-products" />
 *
 * @example Informational only (non-interactive)
 * <Banner message="Holiday orders ship by Dec 20." />
 */
